import * as XLSX from 'xlsx';
import type { ChartData } from '../types/agent.types';

/**
 * Check whether a markdown string contains a GFM table.
 */
export function containsMarkdownTable(markdown: string): boolean {
  return /\|.+\|\s*\n\|[\s\-:|]+\|/.test(markdown);
}

/**
 * Parse the first GFM table found in a markdown string.
 * Returns headers and rows, or null if no table is found.
 *
 * NOTE: this only sees the rows that were rendered in the markdown body,
 * which the chat backend caps at PREVIEW_ROWS (=10). Prefer the ChartData
 * path (`exportTableToExcel(chartData, ...)`) when the response carries
 * structured data — that path exports the FULL row set with no cap.
 */
export function parseMarkdownTable(
  markdown: string
): { headers: string[]; rows: (string | number)[][] } | null {
  const lines = markdown.split('\n');

  let headerIdx = -1;

  // Find header + separator pair
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const next = lines[i + 1].trim();

    if (
      /^\|.+\|$/.test(line) &&
      /^\|[\s\-:|]+\|$/.test(next)
    ) {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx === -1) return null;

  const PLACEHOLDER = '%%ESCAPED_PIPE%%';
  const parseLine = (line: string): string[] => {
    // Replace escaped pipes with a placeholder before splitting
    const safe = line.replace(/\\\|/g, PLACEHOLDER);
    return safe
      .split('|')
      .map((cell) => cell.replace(new RegExp(PLACEHOLDER, 'g'), '|').trim())
      .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1); // drop empty leading/trailing
  };

  const headers = parseLine(lines[headerIdx]);

  const dataRows: (string | number)[][] = [];
  for (let i = headerIdx + 2; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line.startsWith('|')) break; // end of table
    const cells = parseLine(line);
    // Convert numeric-looking cells to numbers for proper Excel formatting
    const typed = cells.map((cell) => {
      const trimmed = cell.replace(/,/g, ''); // strip commas from numbers like "1,234.56"
      const num = Number(trimmed);
      return !isNaN(num) && trimmed !== '' ? num : cell;
    });
    dataRows.push(typed);
  }

  return { headers, rows: dataRows };
}

/**
 * Type guard: does this look like structured ChartData rather than a
 * markdown string?
 */
function isChartData(input: unknown): input is ChartData {
  return (
    !!input &&
    typeof input === 'object' &&
    Array.isArray((input as ChartData).rows) &&
    Array.isArray((input as ChartData).fields)
  );
}

/**
 * Build a worksheet from a ChartData object using ALL rows (no cap) and
 * the field order from `chartData.fields`. Used when the assistant
 * response carries structured data — preserves numeric / date types so
 * Excel formats them natively.
 */
function buildWorksheetFromChartData(chartData: ChartData): XLSX.WorkSheet {
  const headers = chartData.fields.map((f) => f.name);
  const aoa: any[][] = [headers];

  for (const row of chartData.rows) {
    aoa.push(
      headers.map((h) => {
        const v = row[h];
        if (v === null || v === undefined) return '';
        // Numbers / booleans / Date instances passed through unchanged so
        // XLSX preserves their type. Strings that LOOK numeric ($1,234.56)
        // get coerced — same heuristic as the markdown path so the two
        // export sources stay consistent.
        if (typeof v === 'number' || typeof v === 'boolean' || v instanceof Date) {
          return v;
        }
        if (typeof v === 'string') {
          // Strip currency / thousands separators, try a number cast.
          const trimmed = v.replace(/[$,\s]/g, '');
          if (trimmed && !Number.isNaN(Number(trimmed)) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
            return Number(trimmed);
          }
          return v;
        }
        return String(v);
      })
    );
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Auto-size columns based on content width
  const colWidths = headers.map((h, colIdx) => {
    let max = h.length;
    for (let r = 1; r < aoa.length; r++) {
      const cellLen = String(aoa[r][colIdx] ?? '').length;
      if (cellLen > max) max = cellLen;
    }
    return { wch: Math.min(max + 4, 50) }; // cap at 50 chars wide
  });
  ws['!cols'] = colWidths;

  return ws;
}

/**
 * Build a worksheet from a parsed markdown table — fallback path when
 * structured ChartData isn't available.
 */
function buildWorksheetFromMarkdown(markdown: string): XLSX.WorkSheet | null {
  const parsed = parseMarkdownTable(markdown);
  if (!parsed) return null;

  const { headers, rows } = parsed;
  const wsData: (string | number)[][] = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const colWidths = headers.map((h, colIdx) => {
    let max = h.length;
    for (const row of rows) {
      const cellLen = String(row[colIdx] ?? '').length;
      if (cellLen > max) max = cellLen;
    }
    return { wch: Math.min(max + 4, 50) };
  });
  ws['!cols'] = colWidths;

  return ws;
}

/**
 * Export an assistant response's tabular data to an .xlsx file.
 *
 * Prefers the structured ChartData path (FULL row set, native types).
 * Falls back to parsing the markdown body when only that is available —
 * note: the markdown body only carries the first ~10 preview rows, so
 * always prefer ChartData when the response has it.
 *
 * Usage:
 *   exportTableToExcel(m.chartData ?? m.content, "export-...xlsx")
 */
export function exportTableToExcel(
  input: ChartData | string,
  filename?: string
): void {
  let ws: XLSX.WorkSheet | null = null;

  if (isChartData(input)) {
    ws = buildWorksheetFromChartData(input);
  } else if (typeof input === 'string') {
    ws = buildWorksheetFromMarkdown(input);
  }

  if (!ws) return;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');

  const name = filename || `export-${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, name);
}
